import React from 'react';
import { useTranslation } from 'react-i18next';
import { Widget } from '@heroui-pro/react';
import { getNodeTestIdProp } from '@/utils';
import type { ProductDetail } from '@/api/models';
import { ProductDetailBodySkeleton } from './ProductDetailBodySkeleton';
import { ProductDetailField } from './ProductDetailField';
import { ProductDetailHeader } from './ProductDetailHeader';
import { ProductDetailOptions } from './ProductDetailOptions';
import { ProductDetailSectionHeading } from './ProductDetailSectionHeading';
import { ProductVariantPanel } from './ProductVariantPanel';
import { StockScopeNote } from './StockScopeNote';
import { VariantBarcodesDrawer } from './VariantBarcodesDrawer';
import { ProductDraftPanel } from './draftPanel/ProductDraftPanel';
import type { DraftPublisher } from './draftPanel/useDraftPublish';
import {
    PRODUCT_UNIT_LABEL_KEY,
    VAT_TYPE_LABEL_KEY,
    orDash,
} from './productDetailMeta';
import type { ProductDetailBranch } from './useProductDetail';
import type { VariantBarcodesEditor } from './useVariantBarcodesForm';

type ProductDetailViewProps = {
    /** The product; `null` while it loads, when only its content waits. */
    data: ProductDetail | null;
    /** The figures under Variants are the last branch's while the picked one's load. */
    isStockLoading: boolean;
    expandedVariantIds: Set<string>;
    onToggleVariant: (variantId: string) => void;
    onEdit: () => void;
    /** Whose stock the figures count; the header picks it, the labels name it. */
    branch: ProductDetailBranch;
    /** A variant's barcodes drawer, opened from its barcodes card (bos-app#224). */
    barcodes: VariantBarcodesEditor;
    /** A draft's state, issues and Publish; the panel shows while it is one (bos-app#225). */
    draft: DraftPublisher;
};

/**
 * The product details page: the header row, then a draft's panel while the
 * product is one, the summary, the options and the variants, with the barcodes drawer mounted beside them (a variant's
 * barcodes card opens it). The header's Edit and "Stock for" work from the first
 * render; until the product arrives its name and chip, and the content under
 * the header, are placeholders (bos-app#139).
 */
export const ProductDetailView: React.FC<ProductDetailViewProps> = ({
    data,
    isStockLoading,
    expandedVariantIds,
    onToggleVariant,
    onEdit,
    branch,
    barcodes,
    draft,
}) => {
    const { t } = useTranslation('pages');
    const { stockFigures } = branch;

    const copy = React.useMemo(
        () => ({
            summary: {
                sku: t('products.detail.summary.sku'),
                unit: t('products.detail.summary.unit'),
                vat: t('products.detail.summary.vat'),
                category: t('products.detail.summary.category'),
                brand: t('products.detail.summary.brand'),
            },
            variantsTitle: t('products.detail.variants.title'),
        }),
        [t],
    );

    return (
        <div
            className="flex flex-col gap-6"
            {...getNodeTestIdProp('product-detail')}
        >
            <ProductDetailHeader
                branch={branch}
                isDraft={draft.isDraft}
                onEdit={onEdit}
                product={data}
            />

            {data === null ? (
                <ProductDetailBodySkeleton stockFigures={stockFigures} />
            ) : (
                <>
                    {draft.isDraft ? (
                        <ProductDraftPanel onEdit={onEdit} publisher={draft} />
                    ) : null}

                    <Widget
                        className="flex flex-col gap-4 p-5"
                        {...getNodeTestIdProp('product-detail-summary')}
                    >
                        <dl className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3">
                            <ProductDetailField
                                label={copy.summary.sku}
                                slot="sku"
                            >
                                {orDash(data.sku)}
                            </ProductDetailField>
                            <ProductDetailField
                                label={copy.summary.unit}
                                slot="unit"
                            >
                                {t(PRODUCT_UNIT_LABEL_KEY[data.unit])}
                            </ProductDetailField>
                            <ProductDetailField
                                label={copy.summary.vat}
                                slot="vat"
                            >
                                {t(VAT_TYPE_LABEL_KEY[data.vatType])}
                            </ProductDetailField>
                            <ProductDetailField
                                label={copy.summary.category}
                                slot="category"
                            >
                                {orDash(data.category?.name ?? null)}
                            </ProductDetailField>
                            <ProductDetailField
                                label={copy.summary.brand}
                                slot="brand"
                            >
                                {orDash(data.brand?.name ?? null)}
                            </ProductDetailField>
                        </dl>
                    </Widget>

                    {data.options.length > 0 ? (
                        <ProductDetailOptions options={data.options} />
                    ) : null}

                    {/* Busy while a newly picked branch's figures load: the
                        last branch's stay on screen meanwhile, as a refetch
                        keeps its result (rulebook §12), and the product list
                        does the same. */}
                    <section
                        aria-busy={isStockLoading}
                        className="flex flex-col gap-3"
                        {...getNodeTestIdProp('product-detail-variants')}
                    >
                        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                            <ProductDetailSectionHeading>
                                {copy.variantsTitle} ({data.variants.length})
                            </ProductDetailSectionHeading>
                            <StockScopeNote stockFigures={stockFigures} />
                        </div>
                        <div className="flex flex-col gap-3">
                            {data.variants.map((variant) => (
                                <ProductVariantPanel
                                    isExpanded={expandedVariantIds.has(
                                        variant.id,
                                    )}
                                    key={variant.id}
                                    onEditBarcodes={barcodes.onEdit}
                                    onToggle={onToggleVariant}
                                    stockFigures={stockFigures}
                                    variant={variant}
                                />
                            ))}
                        </div>
                    </section>
                </>
            )}

            <VariantBarcodesDrawer editor={barcodes} />
        </div>
    );
};
